import React from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import Layout from '../components/ui/Layout';
import { usePermission } from '../hooks/usePermission';
import ApiWithLogging from '../utils/apiWithLogging';
import { formatDateTime } from '../utils/dateUtils';
import {
  RiBuildingLine,
  RiStoreLine,
  RiBarcodeBoxLine,
  RiPriceTag3Line,
  RiSimCardLine,
  RiDeviceLine,
  RiSmartphoneLine,
  RiAddLine,
  RiRefreshLine
} from 'react-icons/ri';

// คอมโพเนนต์การ์ดเมนู
interface MenuCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  count?: number;
}

const MenuCard: React.FC<MenuCardProps> = ({ title, description, icon, link, count }) => {
  return (
    <Link
      to={link}
      className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200 flex flex-col h-full relative overflow-hidden border border-secondary-200"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
          {icon}
        </div>
        {count !== undefined && (
          <span className="text-sm font-medium px-2.5 py-0.5 rounded-full bg-info-100 text-info-800">
            {count}
          </span>
        )}
      </div>
      <h3 className="text-lg font-medium text-secondary-900 mb-1">{title}</h3>
      <p className="text-sm text-secondary-500 flex-grow">{description}</p>
      <div className="mt-4 flex justify-end">
        <span className="inline-flex items-center text-primary-600 text-sm font-medium hover:text-primary-800">
          จัดการข้อมูล
          <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

const MasterDataPage: React.FC = () => {
  const { hasPermission } = usePermission();

  // ข้อมูลเมนู — G-03: แต่ละการ์ดระบุ permission ตรงกับ route guard ปลายทาง
  // แล้วกรองก่อน render (pattern เดียวกับ SettingsPage) กันคลิกแล้วเด้ง access-denied
  const menuItems = [
    {
      title: 'ข้อมูลบริษัท',
      description: 'จัดการข้อมูลบริษัทที่เกี่ยวข้องกับการเช่าอุปกรณ์',
      icon: <RiBuildingLine className="w-6 h-6" />,
      link: '/master-data/companies',
      countEndpoint: '/companies/',
      permission: 'companies.view'
    },
    {
      title: 'ข้อมูลโครงการ',
      description: 'จัดการข้อมูลโครงการหรือสถานที่ติดตั้งอุปกรณ์',
      icon: <RiStoreLine className="w-6 h-6" />,
      link: '/master-data/sites',
      countEndpoint: '/sites/',
      permission: 'sites.view'
    },
    {
      title: 'ข้อมูลสินค้า',
      description: 'จัดการข้อมูลสินค้าสำหรับการเช่า',
      icon: <RiBarcodeBoxLine className="w-6 h-6" />,
      link: '/master-data/products',
      countEndpoint: '/products/',
      permission: 'products.view'
    },
    {
      title: 'ข้อมูลยี่ห้อ',
      description: 'จัดการข้อมูลยี่ห้อสำหรับอุปกรณ์',
      icon: <RiPriceTag3Line className="w-6 h-6" />,
      link: '/master-data/brands',
      countEndpoint: '/brands/',
      permission: 'brands.view'
    },
    {
      title: 'ประเภทอุปกรณ์',
      description: 'จัดการประเภทอุปกรณ์',
      icon: <RiDeviceLine className="w-6 h-6" />,
      link: '/master-data/device-types',
      countEndpoint: '/device-types/',
      permission: 'device_types.view'
    },
    {
      title: 'ข้อมูล SIM',
      description: 'จัดการข้อมูลซิมการ์ดสำหรับอุปกรณ์',
      icon: <RiSimCardLine className="w-6 h-6" />,
      link: '/master-data/sims',
      countEndpoint: '/sims/',
      permission: 'sims.view'
    },
    {
      title: 'ข้อมูลอุปกรณ์',
      description: 'จัดการข้อมูลอุปกรณ์และสถานะการใช้งาน',
      icon: <RiSmartphoneLine className="w-6 h-6" />,
      link: '/master-data/devices',
      countEndpoint: '/devices/',
      permission: 'devices.view'
    }
  ];

  // กรองเฉพาะการ์ดที่ผู้ใช้มีสิทธิ์เข้าถึง (G-03)
  const visibleMenuItems = menuItems.filter(item => hasPermission(item.permission));

  // BUG-S2002-001 (KD3 2026-09-02): ตัวเลขบนการ์ด/สรุป "ข้อมูลล่าสุด" เคย hardcode (12/28/67/…)
  // → ดึง `total` จาก list endpoint ของแต่ละ entity (limit=1) เฉพาะการ์ดที่ผู้ใช้มีสิทธิ์
  // ตามกติกา "list-page stats ต้องมาจาก backend aggregate" (Decision Register)
  const countQueries = useQueries({
    queries: visibleMenuItems.map(item => ({
      queryKey: ['master-data-count', item.countEndpoint],
      // ApiWithLogging.get คืน body ตรงๆ (ไม่ใช่ axios response) · react-query ห้ามคืน undefined → ใช้ null = ไม่ทราบ
      queryFn: async (): Promise<number | null> => {
        const body = await ApiWithLogging.get<{ total?: number }>(
          item.countEndpoint,
          { params: { page: 1, limit: 1 } },
          { service: 'masterDataPage', action: 'count_master_data', entityType: 'master_data' }
        );
        const total = body?.total;
        return typeof total === 'number' ? total : null;
      },
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const countByLink: Record<string, number | undefined> = {};
  let lastUpdatedAt = 0;
  visibleMenuItems.forEach((item, idx) => {
    const q = countQueries[idx];
    countByLink[item.link] = typeof q?.data === 'number' ? q.data : undefined;
    if (q?.dataUpdatedAt && q.dataUpdatedAt > lastUpdatedAt) lastUpdatedAt = q.dataUpdatedAt;
  });
  const countText = (link: string): string => {
    const v = countByLink[link];
    return typeof v === 'number' ? v.toLocaleString('th-TH') : '—';
  };
  const lastUpdatedText = lastUpdatedAt ? formatDateTime(new Date(lastUpdatedAt).toISOString()) : '—';

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiBarcodeBoxLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ข้อมูลหลัก (Master Data)</h1>
              <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลพื้นฐานที่จำเป็นสำหรับระบบจัดการการเช่าอุปกรณ์</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg mb-6 p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4">ข้อมูลล่าสุด</h2>
          <div className="flex flex-wrap gap-4">
            <div className="bg-info-50 rounded-lg p-4 flex items-center w-full sm:w-auto">
              <RiStoreLine className="w-8 h-8 text-info-500 mr-3" />
              <div>
                <p className="text-sm text-info-800">โครงการ</p>
                <p className="text-2xl font-semibold text-info-900" data-testid="md-summary-sites">{countText('/master-data/sites')}</p>
              </div>
            </div>
            <div className="bg-accent-50 rounded-lg p-4 flex items-center w-full sm:w-auto">
              <RiBarcodeBoxLine className="w-8 h-8 text-accent-500 mr-3" />
              <div>
                <p className="text-sm text-accent-800">สินค้า</p>
                <p className="text-2xl font-semibold text-accent-900" data-testid="md-summary-products">{countText('/master-data/products')}</p>
              </div>
            </div>
            <div className="bg-warning-50 rounded-lg p-4 flex items-center w-full sm:w-auto">
              <RiDeviceLine className="w-8 h-8 text-warning-500 mr-3" />
              <div>
                <p className="text-sm text-warning-800">อุปกรณ์</p>
                <p className="text-2xl font-semibold text-warning-900" data-testid="md-summary-devices">{countText('/master-data/devices')}</p>
              </div>
            </div>
            <div className="ml-auto text-right flex items-center">
              <span className="text-sm text-secondary-500">อัปเดตล่าสุด:</span>
              <span className="ml-2 text-sm font-medium text-secondary-900" data-testid="md-summary-updated">{lastUpdatedText}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleMenuItems.map((item, index) => (
            <MenuCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              link={item.link}
              count={countByLink[item.link]}
            />
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default MasterDataPage;